import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  Home,
  Package,
  FileText,
  FolderOpen,
  BarChart3,
  Settings,
  Users,
  LogOut,
  Bell,
  Search,
  ChevronDown,
  Sun,
  Moon,
  UserPlus,
  Tag,
  MapPin,
  Database,
  Globe,
  Settings as SettingsIcon,
  Clock,
  Shield,
  RefreshCw,
  ClipboardList
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import LoadingSpinner from '../UI/LoadingSpinner';
import { apiService } from '../../services/apiService';
import toast from 'react-hot-toast';
import { getLogoUrl, handleLogoError } from '../../utils/logoUtils';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const searchRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const { isDark, toggleDarkMode } = useTheme();
  const { settings } = useSettings();

  type AppNotification = {
    id: string;
    message: string;
    type?: 'requisition_created';
    requisitionId?: string;
    notificationId?: string;
  };
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);

  const fetchAppNotifications = useCallback(async () => {
    try {
      const res = await apiService.get('/notifications');
      const list = (res.data?.notifications || []).map((n: any) => ({
        id: n._id,
        message: n.type === 'requisition_created' && n.data
          ? `${(n.data.createdBy?.firstName || '')} ${(n.data.createdBy?.lastName || '')}`.trim() + ` has created a new Requisition, "${n.data.requisitionNumber}" at ${new Date(n.data.createdAt).toLocaleString()}`
          : (n.message || 'Notification'),
        type: 'requisition_created' as const,
        requisitionId: n.data?.requisitionId,
        notificationId: n._id
      }));
      return list;
    } catch {
      return [];
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const [appNotifs, invoicesRes] = await Promise.all([
        fetchAppNotifications(),
        apiService.get('/invoices').catch(() => ({ data: { invoices: [] } }))
      ]);
      const invoices = invoicesRes.data?.invoices || [];
      const now = new Date();
      const overdueInvoices: AppNotification[] = invoices.filter((inv: any) => {
        const status = inv.status?.toLowerCase();
        if (status !== 'processing' && status !== 'sent') return false;
        const updated = new Date(inv.updatedAt || inv.createdAt);
        const days = (now.getTime() - updated.getTime()) / (1000 * 60 * 60 * 24);
        return days > 5;
      }).map((inv: any) => ({
        id: `invoice-${inv._id}`,
        message: `Invoice ${inv.invoiceNumber} (${inv.type}) is '${inv.status}' for more than 5 days.`
      }));
      setNotifications([...appNotifs, ...overdueInvoices]);
    } catch (err) {
      setNotifications([]);
    }
  }, [fetchAppNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleClearAllNotifications = async () => {
    try {
      await apiService.delete('/notifications/clear-all').catch(() => {});
      setNotifications([]);
    } catch {
      setNotifications([]);
    }
  };

  const handleDismissNotification = async (e: React.MouseEvent, n: AppNotification) => {
    e.stopPropagation();
    try {
      if (n.notificationId) {
        await apiService.delete(`/notifications/${n.notificationId}`).catch(() => {});
      }
      setNotifications((prev) => prev.filter((item) => item.id !== n.id));
    } catch {
      setNotifications((prev) => prev.filter((item) => item.id !== n.id));
    }
  };

  const handleNotificationClick = useCallback((n: AppNotification) => {
    if (n.type === 'requisition_created' && n.requisitionId) {
      setNotificationOpen(false);
      navigate('/requisitions', { state: { viewRequisitionId: n.requisitionId } });
      if (n.notificationId) {
        apiService.patch(`/notifications/${n.notificationId}/read`).catch(() => {});
      }
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user) return;
    const baseURL = process.env.REACT_APP_API_URL || 'https://aircons-inventory-backend.onrender.com/api';
    const socketUrl = baseURL.replace(/\/api\/?$/, '');
    const socket = io(socketUrl, {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;
    socket.on('requisition_created', (payload: { message?: string; _id?: string; requisitionId?: string; requisitionNumber?: string; createdBy?: { firstName?: string; lastName?: string }; createdAt?: string }) => {
      const message = payload.message || (payload.createdBy && `${(payload.createdBy.firstName || '')} ${(payload.createdBy.lastName || '')}`.trim() + ` has created a new Requisition, "${payload.requisitionNumber || ''}" at ${payload.createdAt ? new Date(payload.createdAt).toLocaleString() : ''}`) || 'A new requisition has been created.';
      toast(message, { duration: 6000 });
      setNotifications((prev) => {
        const newNotif: AppNotification = {
          id: payload._id || `req-${payload.requisitionId}`,
          message,
          type: 'requisition_created',
          requisitionId: payload.requisitionId,
          notificationId: payload._id
        };
        return [newNotif, ...prev.filter((p) => p.id !== newNotif.id)];
      });
    });
    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [user]);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationOpen(false);
      }
    }
    if (notificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationOpen]);

  // Navigation items with correct permissions (Requisition right after Dashboard)
  const allNavigationItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: Home,
      permission: 'dashboard.read'
    },
    {
      name: 'Requisitions',
      path: '/requisitions',
      icon: ClipboardList,
      permission: 'requisitions.read'
    },
    {
      name: 'Invoices',
      path: '/invoices',
      icon: FileText,
      permission: 'invoices.read',
      children: [
        {
          name: 'Purchase Invoices',
          path: '/invoices/purchase',
          icon: FileText,
          permission: 'invoices.read',
        },
        {
          name: 'Delivery Invoices',
          path: '/invoices/delivery',
          icon: FileText,
          permission: 'invoices.read',
        },
        {
          name: 'Return Invoices',
          path: '/invoices/return',
          icon: FileText,
          permission: 'invoices.read',
        },
      ]
    },
    {
      name: 'Products',
      path: '/products',
      icon: Package,
      permission: 'products.read'
    },
    {
      name: 'Customers',
      path: '/customers',
      icon: UserPlus,
      permission: 'customers.read'
    },
    {
      name: 'Projects',
      path: '/projects',
      icon: FolderOpen,
      permission: 'projects.read'
    },
    {
      name: 'Suppliers',
      path: '/suppliers',
      icon: Package,
      permission: 'suppliers.read'
    },
    {
      name: 'Other Entities',
      path: '/other-entities',
      icon: Database,
      permission: 'categories.read',
      children: [
        {
          name: 'Product Categories',
          path: '/product-categories',
          icon: Tag,
          permission: 'categories.read',
        },
        {
          name: 'Locations',
          path: '/locations',
          icon: MapPin,
          permission: 'locations.read',
        },
        {
          name: 'Brands',
          path: '/brands',
          icon: Tag,
          permission: 'brands.read',
        },
        {
          name: 'Types',
          path: '/types',
          icon: Tag,
          permission: 'productTypes.read',
        },
        {
          name: 'Compressor Types',
          path: '/compressor-types',
          icon: SettingsIcon,
          permission: 'compressorTypes.read',
        },
        {
          name: 'Countries',
          path: '/countries',
          icon: Globe,
          permission: 'countries.read',
        },
        {
          name: 'Units',
          path: '/units',
          icon: Tag,
          permission: 'units.read',
        },
      ]
    },
    {
      name: 'Employees',
      path: '/employees',
      icon: Users,
      permission: 'employees.read'
    },
    {
      name: 'Reports',
      path: '/reports',
      icon: BarChart3,
      permission: 'reports.read'
    },
    {
      name: 'Warranty',
      path: '/warranty',
      icon: Shield,
      permission: 'warranty.read'
    },
    {
      name: 'Users',
      path: '/users',
      icon: Users,
      permission: 'users.read'
    },
    {
      name: 'Settings',
      path: '/settings',
      icon: Settings,
      permission: 'settings.read'
    }
  ];

  // Filter navigation items based on permissions only if user is loaded
  // Admin users should see all navigation items
  const navigationItems = user ? allNavigationItems.filter(item => {
    // Admin users always have access to everything
    if (user.role === 'admin') return true;
    
    // For other users, check specific permissions
    const [resource, action] = item.permission.split('.');
    return hasPermission(resource, action);
  }) : [];

  // Requisition users: redirect to /requisitions if they access a page they're not allowed to see
  const requisitionUserAllowedPaths = ['/requisitions', '/products', '/customers', '/projects', '/product-categories', '/locations', '/brands', '/types', '/compressor-types', '/countries', '/units', '/other-entities'];
  useEffect(() => {
    if (user?.role === 'requisition_user' && location.pathname) {
      const allowed = requisitionUserAllowedPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
      if (!allowed) {
        navigate('/requisitions', { replace: true });
      }
    }
  }, [user?.role, location.pathname, navigate]);

  // Enhanced search with comprehensive results
  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setSearchLoading(true);
    try {
      const results: any[] = [];

      // Use the global search API for comprehensive results
      try {
        console.log('Attempting global search for query:', query);
        const searchRes = await apiService.get(`/search/global?query=${encodeURIComponent(query)}&limit=20`);
        const searchData = searchRes.data;
        console.log('Global search response:', searchData);

        // Process products with comprehensive search
        if (searchData.results.products) {
          searchData.results.products.forEach((product: any) => {
            let subtitle = product.sku || '';
            if (product.category) {
              subtitle += subtitle ? ` • ${product.category}` : product.category;
            }
            if (product.brand) {
              subtitle += subtitle ? ` • ${product.brand}` : product.brand;
            }
            
            let description = product.description || '';
            if (product.location) {
              description += description ? ` • Location: ${product.location}` : `Location: ${product.location}`;
            }
            
            results.push({
              type: 'product',
              id: product.id,
              title: product.name,
              subtitle: subtitle || 'No SKU',
              description: description || 'No description',
              path: `/products?search=${encodeURIComponent(product.name)}`
            });
          });
        }

        // Process categories
        if (searchData.results.categories) {
          searchData.results.categories.forEach((category: any) => {
            results.push({
              type: 'category',
              id: category.id,
              title: category.name,
              subtitle: 'Category',
              description: category.description || 'No description',
              path: `/products/categories?search=${encodeURIComponent(category.name)}`
            });
          });
        }

        // Process locations
        if (searchData.results.locations) {
          searchData.results.locations.forEach((location: any) => {
            results.push({
              type: 'location',
              id: location.id,
              title: location.name,
              subtitle: 'Location',
              description: location.address ? `${location.address.street}, ${location.address.city}` : 'No address',
              path: `/products/locations?search=${encodeURIComponent(location.name)}`
            });
          });
        }

        // Process suppliers
        if (searchData.results.suppliers) {
          searchData.results.suppliers.forEach((supplier: any) => {
            results.push({
              type: 'supplier',
              id: supplier.id,
              title: supplier.name,
              subtitle: supplier.email || supplier.phone || 'No contact',
              description: `${supplier.contactPerson || ''} ${supplier.address || ''}`.trim() || 'No details',
              path: `/suppliers?search=${encodeURIComponent(supplier.name)}`
            });
          });
        }

        // Process customers
        if (searchData.results.customers) {
          searchData.results.customers.forEach((customer: any) => {
            results.push({
              type: 'customer',
              id: customer.id,
              title: customer.name,
              subtitle: customer.email || customer.phone || 'No contact',
              description: `${customer.contactPerson || ''} ${customer.address || ''}`.trim() || 'No details',
              path: `/customers?search=${encodeURIComponent(customer.name)}`
            });
          });
        }

        // Process projects
        if (searchData.results.projects) {
          searchData.results.projects.forEach((project: any) => {
            results.push({
              type: 'project',
              id: project.id,
              title: project.name,
              subtitle: project.contact || project.email || 'No contact',
              description: project.description || 'No description',
              path: `/projects/${project.id}`
            });
          });
        }

      } catch (error) {
        console.error('Error in global search:', error);
        console.log('Falling back to individual searches...');
        
        // Fallback to individual searches if global search fails
        // Search products with comprehensive search
        try {
          const productsRes = await apiService.get(`/products?search=${encodeURIComponent(query)}&limit=20`);
          const products = productsRes.data.products || [];
          console.log('Fallback product search results:', products.length);
          
          products.forEach((product: any) => {
            let subtitle = product.sku || '';
            if (product.category?.name) {
              subtitle += subtitle ? ` • ${product.category.name}` : product.category.name;
            }
            if (product.customFields?.brand) {
              subtitle += subtitle ? ` • ${product.customFields.brand}` : product.customFields.brand;
            }
            if (product.customFields?.model) {
              subtitle += subtitle ? ` • ${product.customFields.model}` : product.customFields.model;
            }
            
            let description = product.description || '';
            if (product.customFields?.compressorType) {
              description += description ? ` • Compressor: ${product.customFields.compressorType}` : `Compressor: ${product.customFields.compressorType}`;
            }
            if (product.customFields?.type) {
              description += description ? ` • Type: ${product.customFields.type}` : `Type: ${product.customFields.type}`;
            }
            if (product.customFields?.specification) {
              description += description ? ` • Spec: ${product.customFields.specification}` : `Spec: ${product.customFields.specification}`;
            }
            if (product.location?.name) {
              description += description ? ` • Location: ${product.location.name}` : `Location: ${product.location.name}`;
            }
            
            results.push({
              type: 'product',
              id: product._id,
              title: product.name,
              subtitle: subtitle || 'No SKU',
              description: description || 'No description',
              path: `/products?search=${encodeURIComponent(product.name)}`
            });
          });
        } catch (error) {
          console.error('Error searching products:', error);
        }

      // Search invoices
      try {
        const invoicesRes = await apiService.get(`/invoices?search=${encodeURIComponent(query)}&limit=10`);
        const invoices = invoicesRes.data.invoices || [];
        invoices.forEach((invoice: any) => {
          results.push({
            type: 'invoice',
            id: invoice._id,
            title: `Invoice #${invoice.invoiceNumber}`,
            subtitle: invoice.type,
            description: invoice.customer?.name || invoice.customerName,
            path: `/invoices/${invoice._id}`
          });
        });
      } catch (error) {
        console.error('Error searching invoices:', error);
      }

      // Search projects
      try {
        const projectsRes = await apiService.get(`/projects?search=${encodeURIComponent(query)}&limit=10`);
        const projects = projectsRes.data.projects || [];
        projects.forEach((project: any) => {
          results.push({
            type: 'project',
            id: project._id,
            title: project.name,
            subtitle: project.customerName,
            description: project.description,
            path: `/projects/${project._id}`
          });
        });
      } catch (error) {
        console.error('Error searching projects:', error);
      }

      // Search customers
      try {
        const customersRes = await apiService.get(`/customers?search=${encodeURIComponent(query)}&limit=10`);
        const customers = customersRes.data.customers || [];
        customers.forEach((customer: any) => {
          // Build comprehensive subtitle with contact details
          let subtitle = customer.email || '';
          if (customer.phone) {
            subtitle += subtitle ? ` • ${customer.phone}` : customer.phone;
          }
          if (customer.contactPerson) {
            subtitle += subtitle ? ` • ${customer.contactPerson}` : customer.contactPerson;
          }
          
          // Build description with address and other details
          let description = customer.address || '';
          if (customer.email && !subtitle.includes(customer.email)) {
            description += description ? ` • ${customer.email}` : customer.email;
          }
          
          results.push({
            type: 'customer',
            id: customer._id,
            title: customer.name,
            subtitle: subtitle || 'No contact info',
            description: description || 'No address',
            path: `/customers`
          });
        });
      } catch (error) {
        console.error('Error searching customers:', error);
      }

      // Search suppliers
      try {
        const suppliersRes = await apiService.get(`/suppliers?search=${encodeURIComponent(query)}&limit=10`);
        const suppliers = suppliersRes.data.suppliers || [];
        suppliers.forEach((supplier: any) => {
          // Build comprehensive subtitle with contact details
          let subtitle = supplier.email || '';
          if (supplier.phone) {
            subtitle += subtitle ? ` • ${supplier.phone}` : supplier.phone;
          }
          if (supplier.contactPerson) {
            subtitle += subtitle ? ` • ${supplier.contactPerson}` : supplier.contactPerson;
          }
          
          // Build description with address and other details
          let description = supplier.address || '';
          if (supplier.email && !subtitle.includes(supplier.email)) {
            description += description ? ` • ${supplier.email}` : supplier.email;
          }
          
          results.push({
            type: 'supplier',
            id: supplier._id,
            title: supplier.name,
            subtitle: subtitle || 'No contact info',
            description: description || 'No address',
            path: `/suppliers`
          });
        });
      } catch (error) {
        console.error('Error searching suppliers:', error);
      }

      // Search employees
      try {
        const employeesRes = await apiService.get(`/employees?search=${encodeURIComponent(query)}&limit=10`);
        const employees = employeesRes.data.employees || [];
        employees.forEach((employee: any) => {
          // Build comprehensive subtitle with contact details
          let subtitle = employee.email || '';
          if (employee.phone) {
            subtitle += subtitle ? ` • ${employee.phone}` : employee.phone;
          }
          if (employee.employeeId) {
            subtitle += subtitle ? ` • ID: ${employee.employeeId}` : `ID: ${employee.employeeId}`;
          }
          
          // Build description with position and department
          let description = employee.position || '';
          if (employee.department) {
            description += description ? ` • ${employee.department}` : employee.department;
          }
          if (employee.designation) {
            description += description ? ` • ${employee.designation}` : employee.designation;
          }
          
          results.push({
            type: 'employee',
            id: employee._id,
            title: employee.name,
            subtitle: subtitle || 'No contact info',
            description: description || 'No position info',
            path: `/employees`
          });
        });
      } catch (error) {
        console.error('Error searching employees:', error);
      }

      // Search categories
      try {
        const categoriesRes = await apiService.get(`/categories?search=${encodeURIComponent(query)}&limit=10`);
        const categories = categoriesRes.data.categories || [];
        categories.forEach((category: any) => {
          // Build comprehensive subtitle with description
          let subtitle = category.description || '';
          if (category.parentCategory?.name) {
            subtitle += subtitle ? ` • Parent: ${category.parentCategory.name}` : `Parent: ${category.parentCategory.name}`;
          }
          
          // Build description with additional details
          let description = category.parentCategory?.name ? `Sub-category of ${category.parentCategory.name}` : 'Main Category';
          if (category.description && !subtitle.includes(category.description)) {
            description += ` • ${category.description}`;
          }
          
          results.push({
            type: 'category',
            id: category._id,
            title: category.name,
            subtitle: subtitle || 'No description',
            description: description,
            path: `/products/categories`
          });
        });
      } catch (error) {
        console.error('Error searching categories:', error);
      }

      // Search brands
      try {
        const brandsRes = await apiService.get(`/brands?search=${encodeURIComponent(query)}&limit=10`);
        const brands = brandsRes.data.brands || [];
        brands.forEach((brand: any) => {
          // Build comprehensive subtitle with description
          let subtitle = brand.description || '';
          if (brand.countryOfOrigin) {
            subtitle += subtitle ? ` • ${brand.countryOfOrigin}` : brand.countryOfOrigin;
          }
          
          // Build description with additional details
          let description = brand.countryOfOrigin ? `Origin: ${brand.countryOfOrigin}` : '';
          if (brand.description && !subtitle.includes(brand.description)) {
            description += description ? ` • ${brand.description}` : brand.description;
          }
          
          results.push({
            type: 'brand',
            id: brand._id,
            title: brand.name,
            subtitle: subtitle || 'No description',
            description: description || 'No details',
            path: `/products/brands`
          });
        });
      } catch (error) {
        console.error('Error searching brands:', error);
      }

      // Search locations
      try {
        const locationsRes = await apiService.get(`/locations?search=${encodeURIComponent(query)}&limit=10`);
        const locations = locationsRes.data.locations || [];
        locations.forEach((location: any) => {
          // Build comprehensive subtitle with address
          let subtitle = '';
          if (typeof location.address === 'object' && location.address !== null) {
            subtitle = location.address.street || location.address.toString() || '';
          } else {
            subtitle = location.address || '';
          }
          
          // Build description with additional details
          let description = location.description || '';
          if (location.address && !subtitle.includes(location.address)) {
            description += description ? ` • ${location.address}` : location.address;
          }
          
          results.push({
            type: 'location',
            id: location._id,
            title: location.name,
            subtitle: subtitle || 'No address',
            description: description || 'No description',
            path: `/products/locations`
          });
        });
      } catch (error) {
        console.error('Error searching locations:', error);
      }

      // Search users
      try {
        const usersRes = await apiService.get(`/users?search=${encodeURIComponent(query)}&limit=10`);
        const users = usersRes.data.users || [];
        users.forEach((user: any) => {
          // Build comprehensive subtitle with contact details
          let subtitle = user.email || '';
          if (user.firstName && user.lastName) {
            subtitle += subtitle ? ` • ${user.firstName} ${user.lastName}` : `${user.firstName} ${user.lastName}`;
          }
          
          // Build description with role and additional details
          let description = user.role || '';
          if (user.email && !subtitle.includes(user.email)) {
            description += description ? ` • ${user.email}` : user.email;
          }
          if (user.isAdmin) {
            description += description ? ' • Admin' : 'Admin';
          }
          
          results.push({
            type: 'user',
            id: user._id,
            title: user.username,
            subtitle: subtitle || 'No contact info',
            description: description || 'No role info',
            path: `/users`
          });
        });
      } catch (error) {
        console.error('Error searching users:', error);
      }
      } // End of fallback search

      setSearchResults(results);
      setShowSearchDropdown(true); // Always show dropdown when searching
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (query.trim()) {
      // Set new timeout for debounced search
      const timeout = setTimeout(() => {
        performSearch(query);
      }, 150); // 150ms delay for faster response
      setSearchTimeout(timeout);
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
    }
  };

  // Handle search form submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Only clear search if no results are found
      if (searchResults.length === 0) {
      setSearchQuery('');
      }
      setShowSearchDropdown(false);
    }
  };

  // Enhanced search result click handler with proper filtering
  const handleSearchResultClick = (result: any) => {
    // Navigate to the appropriate page with search filter
    let targetPath = result.path;
    
    // For direct navigation items (projects, invoices), use the direct path
    if (result.type === 'project' || result.type === 'invoice') {
      targetPath = result.path;
    } else {
      // For list pages, add search filter to show only the specific item
      const searchTerm = result.title;
      const encodedSearch = encodeURIComponent(searchTerm);
      
      switch (result.type) {
        case 'product':
          targetPath = `/products?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'customer':
          targetPath = `/customers?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'supplier':
          targetPath = `/suppliers?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'employee':
          targetPath = `/employees?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'category':
          targetPath = `/products/categories?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'brand':
          targetPath = `/products/brands?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'location':
          targetPath = `/products/locations?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'type':
          targetPath = `/products/types?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'compressorType':
          targetPath = `/products/compressor-types?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'country':
          targetPath = `/products/countries?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'unit':
          targetPath = `/products/units?search=${encodedSearch}&highlight=${result.id}`;
          break;
        case 'user':
          targetPath = `/users?search=${encodedSearch}&highlight=${result.id}`;
          break;
        default:
          targetPath = result.path;
      }
    }
    
    navigate(targetPath);
    setSearchQuery('');
    setShowSearchDropdown(false);
  };

  // Close search dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    }
    
    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchDropdown]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (!user) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] transition-colors duration-300" style={{ color: 'var(--color-text)', fontFamily: 'var(--font-family)' } as any}>
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 } as any}
              animate={{ opacity: 1 } as any}
              exit={{ opacity: 0 } as any}
              className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            {/* Sidebar */}
            <motion.aside
              initial={{ x: -300 } as any}
              animate={{ x: 0 } as any}
              exit={{ x: -300 } as any}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 h-full w-4/5 max-w-xs bg-[var(--color-surface)] border-r border-[var(--color-border)] shadow-xl z-50 lg:hidden"
            >
              <SidebarContent 
                navigationItems={navigationItems}
                user={user}
                onClose={() => setSidebarOpen(false)}
                onLogout={handleLogout}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-80 lg:fixed lg:inset-y-0 lg:z-50">
        <div className="flex flex-col flex-grow bg-[var(--color-surface)] border-r border-[var(--color-border)] shadow-xl">
          <SidebarContent 
            navigationItems={navigationItems}
            user={user}
            onClose={() => { /* intentionally left empty */ }}
            onLogout={handleLogout}
          />
        </div>
      </aside>
      {/* Main Content */}
      <div className="lg:pl-80">
        {/* Header */}
        <header className="bg-[var(--color-surface)] shadow-sm border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3">
            {/* Left side */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-[var(--color-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                aria-label="Open sidebar"
              >
                <Menu size={20} />
              </button>
              {/* Company Logo/Name */}
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-[var(--color-text)]" style={{ fontFamily: 'var(--font-family)' } as any}>
                  {settings?.company?.name || 'The Aircons Ltd.'}
                </h1>
                <p className="text-xs text-[var(--color-text-secondary)]" style={{ fontFamily: 'var(--font-family)' } as any}>
                  Inventory Management System
                </p>
              </div>
              {/* Clock */}
              <div className="flex items-center space-x-2 text-sm text-[var(--color-text-secondary)]">
                <Clock size={16} />
                <div className="text-center">
                  <div className="font-medium text-[var(--color-text)]">
                    {currentTime.toLocaleTimeString('en-US', { 
                      hour12: true, 
                      hour: '2-digit', 
                      minute: '2-digit', 
                      second: '2-digit' 
                    })}
                  </div>
                  <div className="text-xs hidden sm:block">
                    {currentTime.toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>
              </div>
            </div>
            {/* Center - Search */}
            <div className="flex-1 max-w-xs sm:max-w-lg mx-2 sm:mx-4" ref={searchRef}>
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-secondary)]" size={16} />
                <input
                  placeholder="Search products, invoices, projects..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-20 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] placeholder-[var(--color-text-secondary)] focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[var(--color-text-secondary)] hidden sm:block">
                  ⌘K
                </div>
                
                {/* Search Dropdown */}
                {showSearchDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto scrollbar-thin">
                    {searchLoading ? (
                      <div className="p-4 text-center text-[var(--color-text-secondary)]">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Searching...</span>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div>
                        {searchResults.map((result, index) => (
                          <div
                            key={`${result.type}-${result.id}`}
                            onClick={() => handleSearchResultClick(result)}
                            className="p-3 hover:bg-[var(--color-background)] cursor-pointer border-b border-[var(--color-border)] last:border-b-0 transition-colors"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                {result.type === 'product' && <Package className="w-4 h-4 text-blue-500" />}
                                {result.type === 'invoice' && <FileText className="w-4 h-4 text-green-500" />}
                                {result.type === 'project' && <FolderOpen className="w-4 h-4 text-purple-500" />}
                                {result.type === 'customer' && <Users className="w-4 h-4 text-orange-500" />}
                                {result.type === 'supplier' && <Tag className="w-4 h-4 text-red-500" />}
                                {result.type === 'employee' && <UserPlus className="w-4 h-4 text-indigo-500" />}
                                {result.type === 'category' && <Database className="w-4 h-4 text-teal-500" />}
                                {result.type === 'brand' && <Globe className="w-4 h-4 text-yellow-500" />}
                                {result.type === 'location' && <MapPin className="w-4 h-4 text-pink-500" />}
                                {result.type === 'user' && <SettingsIcon className="w-4 h-4 text-gray-500" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-[var(--color-text)] truncate">
                                  {result.title}
                                </div>
                                {result.subtitle && (
                                  <div className="text-xs text-[var(--color-text-secondary)] truncate">
                                    {result.subtitle}
                                  </div>
                                )}
                                {result.description && (
                                  <div className="text-xs text-[var(--color-text-secondary)] truncate mt-1">
                                    {result.description}
                                  </div>
                                )}
                              </div>
                              <div className="flex-shrink-0">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[var(--color-background)] text-[var(--color-text)] border border-[var(--color-border)] capitalize">
                                  {result.type}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="p-3 text-center text-xs text-[var(--color-text-secondary)] border-t border-[var(--color-border)] bg-[var(--color-background)]">
                          <div className="font-medium text-[var(--color-text)]">Found {searchResults.length} results</div>
                          <div>Click on a result to navigate</div>
                        </div>
                      </div>
                    ) : searchQuery.trim() && !searchLoading ? (
                      <div className="p-4 text-center text-[var(--color-text-secondary)]">
                        <Search className="w-5 h-5 mx-auto mb-2 text-[var(--color-text-secondary)]" />
                        <div className="text-sm font-medium text-[var(--color-text)]">Nothing Found</div>
                        <div className="text-xs">Try searching for something else</div>
                      </div>
                    ) : null}
                  </div>
                )}
              </form>
            </div>
            {/* Right side */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Global refresh */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Strictly clear client cache only; do not navigate, reload, or emit events
                  apiService.clearCache();
                  toast.success('Cache cleared');
                }}
                className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-background)] hover:text-[var(--color-text)] transition-colors bg-transparent"
                title="Refresh data"
              >
                <RefreshCw size={18} />
              </button>
              {/* Theme toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-background)] hover:text-[var(--color-text)] transition-colors"
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              {/* Notifications */}
              <div className="relative" ref={notificationRef}>
                <button
                  className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-background)] hover:text-[var(--color-text)] transition-colors relative"
                  onClick={() => setNotificationOpen((open) => !open)}
                >
                  <Bell size={20} />
                  {notifications.length > 0 && (
                    <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
                {notificationOpen && (
                  <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-[var(--color-surface)] rounded-xl shadow-2xl border border-[var(--color-border)] z-50 overflow-hidden flex flex-col">
                    <div className="p-3 sm:p-4 border-b border-[var(--color-border)] flex items-center justify-between bg-[var(--color-surface)]">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[var(--color-text)]">Notifications</span>
                        {notifications.length > 0 && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                            {notifications.length}
                          </span>
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <button
                          onClick={handleClearAllNotifications}
                          className="text-xs text-[var(--color-primary)] hover:text-[var(--color-danger)] font-medium transition-colors"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 sm:max-h-96 overflow-y-auto divide-y divide-[var(--color-border)]">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-[var(--color-text-secondary)] text-sm">
                          <Bell size={28} className="mx-auto mb-2 opacity-40" />
                          No notifications
                        </div>
                      ) : (
                        notifications.map((n) => (
                          <div
                            key={n.id}
                            role={n.requisitionId ? 'button' : undefined}
                            onClick={() => n.requisitionId && handleNotificationClick(n)}
                            className={`p-3 sm:p-4 hover:bg-[var(--color-surface-hover)] transition-colors text-left text-sm flex items-start justify-between gap-2 group ${n.requisitionId ? 'cursor-pointer' : ''}`}
                            style={{ fontFamily: 'var(--font-family)' } as any}
                          >
                            <span className="text-[var(--color-text)] leading-snug flex-1">{n.message}</span>
                            <button
                              onClick={(e) => handleDismissNotification(e, n)}
                              className="text-[var(--color-text-secondary)] hover:text-[var(--color-danger)] opacity-60 hover:opacity-100 p-1 rounded transition-colors"
                              title="Dismiss"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* User dropdown */}
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center space-x-2 p-2 rounded-lg text-[var(--color-text)] hover:bg-[var(--color-background)] transition-colors"
                >
                  <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-[var(--color-text)]" style={{ fontFamily: 'var(--font-family)' } as any}>
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-[var(--color-text-secondary)] capitalize" style={{ fontFamily: 'var(--font-family)' } as any}>
                      {user.role}
                    </p>
                  </div>
                </button>
                {/* Dropdown menu remains unchanged */}
              </div>
            </div>
          </div>
        </header>
        {/* Page content */}
        <main className="flex-1">
          <div className="p-2 sm:p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 20 } as any}
                animate={{ opacity: 1, y: 0 } as any}
                exit={{ opacity: 0, y: -20 } as any}
                transition={{ duration: 0.3 }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>
      {/* Close dropdown when clicking outside */}
      {dropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setDropdownOpen(false)}
        />
      )}
    </div>
  );
};

// Sidebar Content Component
interface SidebarContentProps {
  navigationItems: Array<{
    name: string;
    path: string;
    icon: React.ComponentType<any>;
    permission: string;
    children?: Array<{
      name: string;
      path: string;
      icon: React.ComponentType<any>;
      permission: string;
    }>;
  }>;
  user: any;
  onClose: () => void;
  onLogout: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  navigationItems,
  user,
  onClose,
  onLogout
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
        <div className="flex items-center space-x-3">
          <img 
            src="/The Aircons Ltd. Logo Org.png"
            alt={settings?.company?.name || 'The Aircons Ltd.'} 
            className="h-8 w-auto max-h-8"
            onLoad={() => console.log('Company logo loaded successfully')}
            onError={(e) => {
              console.error('Failed to load company logo from:', (e.target as HTMLImageElement).src);
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {/* Removed Inventory System text here */}
        </div>
        
        {/* Close button for mobile */}
        <button
          onClick={onClose}
          className="lg:hidden p-2 rounded-md text-[var(--color-text-secondary)] hover:bg-[var(--color-background)] hover:text-[var(--color-text)] transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const hasChildren = Array.isArray(item.children) && (item.children?.length ?? 0) > 0;
          const isSubmenuOpen = openSubmenu === item.name;
          if (hasChildren) {
            return (
              <div key={item.path}>
                <motion.button
                  onClick={() => setOpenSubmenu(isSubmenuOpen ? null : item.name)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                    isActive || (item.children?.some(child => location.pathname === child.path) ?? false)
                      ? 'bg-[var(--color-primary)] text-white shadow-lg'
                      : 'text-[var(--color-text)] hover:bg-[var(--sidebar-hover-bg)]'
                  }`}
                  whileHover={{ scale: 1.02 } as any}
                  whileTap={{ scale: 0.98 } as any}
                >
                  <Icon size={20} />
                  <span className="font-medium flex-1">{item.name}</span>
                  <ChevronDown size={16} className={`transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`} />
                </motion.button>
                {isSubmenuOpen && (
                  <div className="ml-6 mt-1 space-y-1">
                    {(item.children ?? []).map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <motion.button
                          key={child.path}
                          onClick={() => navigate(child.path)}
                          className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left transition-all duration-200 ${
                            location.pathname === child.path
                              ? 'bg-[var(--color-primary)] text-white shadow-lg'
                              : 'text-[var(--color-text)] hover:bg-[var(--sidebar-hover-bg)]'
                          }`}
                          whileHover={{ scale: 1.02 } as any}
                          whileTap={{ scale: 0.98 } as any}
                        >
                          <ChildIcon size={18} />
                          <span className="font-medium">{child.name}</span>
                        </motion.button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          return (
            <motion.button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                isActive
                  ? 'bg-[var(--color-primary)] text-white shadow-lg'
                  : 'text-[var(--color-text)] hover:bg-[var(--sidebar-hover-bg)]'
              }`}
              whileHover={{ scale: 1.02 } as any}
              whileTap={{ scale: 0.98 } as any}
            >
              <Icon size={20} />
              <span className="font-medium">{item.name}</span>
            </motion.button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-[var(--color-border)]">
        <div
          className="flex items-center space-x-3 p-3 rounded-lg bg-[var(--color-background)] border border-[var(--color-border)] shadow-sm transition-colors duration-300"
          style={{ fontFamily: 'var(--font-family)' } as any}
        >
          <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white font-semibold">
              {user.firstName.charAt(0)}{user.lastName.charAt(0)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--color-text)] truncate" style={{ fontFamily: 'var(--font-family)' } as any}>
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-[var(--color-text-secondary)] capitalize" style={{ fontFamily: 'var(--font-family)' } as any}>
              {user.role}
            </p>
          </div>
          <button
            onClick={onLogout}
            className="p-2 rounded-md text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface)] transition-colors"
            title="Logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Layout; 